import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { ApplicationContext } from '../context/ApplicationContext';

const Apply = ({ onLoginSuccess }) => {
  const { applications, setApplications } = useContext(ApplicationContext);

  // NOTE: This component used to have its own separate username/password
  // login form (a different pattern from the rest of the admin panel).
  // Access to /admin/hr is now gated the same way as Projects, Gallery,
  // CSR and Policies — via the sidebar's password popup in AdminLayout —
  // so that second, inconsistent login screen has been removed. This
  // component now renders the dashboard directly.
  useEffect(() => {
    if (onLoginSuccess) onLoginSuccess();
  }, [onLoginSuccess]);

  const [formData, setFormData] = useState({
    positionName: '',
    qualification: '',
    experience: '',
    location: '',
    expertise: '',
    skills: [],
    numberOfOpenings: '',
    jobDescription: '',
    postingDate: new Date().toISOString().split('T')[0],
    isActive: true,
    closingDate: '',
  });
  const [showApplications, setShowApplications] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs`)
      .then((res) => {
        const currentDate = new Date();
        const updatedJobs = res.data.map((job) => {
          if (job.closingDate && new Date(job.closingDate) < currentDate) {
            return { ...job, isActive: false };
          }
          return job;
        });
        setApplications(updatedJobs);
      })
      .catch((err) => console.error('Error fetching jobs:', err));
  }, [setApplications]);

  // FIX: previously this reset `skills` to [] every time you typed into
  // the Position field. That silently wiped the job's skills on every
  // keystroke, which then failed the backend's `Array.isArray(skills) &&
  // skills.length > 0` validation on submit — causing the whole PUT/POST
  // to be rejected (400) any time Position was touched, with no visible
  // error to the user. Removed the reset entirely.
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      positionName: '',
      qualification: '',
      experience: '',
      location: '',
      expertise: '',
      skills: [],
      numberOfOpenings: '',
      jobDescription: '',
      postingDate: new Date().toISOString().split('T')[0],
      isActive: true,
      closingDate: '',
    });
    setEditIndex(null);
  };

  const handleApplicationSubmit = (e) => {
    e.preventDefault();

    const currentDate = new Date();

    const closingDate = formData.closingDate
      ? new Date(formData.closingDate).toISOString()
      : null;

    // Build sanitized job data.
    // NOTE: backend requires `skills` to be an ARRAY
    // (`Array.isArray(skills)` check in POST /api/jobs and PUT
    // /api/jobs/:id) — keep it as an array all the way through, never
    // join it into a comma-separated string here.
    const jobData = {
      positionName: formData.positionName?.trim() || '',
      qualification: formData.qualification?.trim() || '',
      experience: formData.experience?.trim() || '',
      location: formData.location?.trim() || '',
      skills: Array.isArray(formData.skills)
        ? formData.skills.map((s) => s.trim()).filter((s) => s !== '')
        : [],
      numberOfOpenings: formData.numberOfOpenings?.toString() || '1',
      jobDescription: formData.jobDescription?.trim() || '',
      postingDate: formData.postingDate
        ? new Date(formData.postingDate).toISOString()
        : new Date().toISOString(),
      closingDate: formData.closingDate
        ? new Date(formData.closingDate).toISOString()
        : '',
      expertise: formData.expertise?.trim() || '',
      isActive:
        closingDate && new Date(closingDate) < currentDate
          ? false
          : true,
    };

    if (editIndex !== null) {
      const jobId = applications[editIndex].id;
      axios
        .put(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/${jobId}`, jobData)
        .then((res) => {
          const updatedApps = [...applications];
          updatedApps[editIndex] = res.data.job;
          setApplications(updatedApps);
          resetForm();
        })
        .catch((err) => {
          console.error('Error updating job:', err.response?.data || err.message);
          alert(err.response?.data?.error || 'Failed to update job. Please try again.');
        });
    } else {
      axios
        .post(`${process.env.REACT_APP_API_BASE_URL}/api/jobs`, jobData)
        .then((res) => {
          setApplications([...applications, res.data.job]);
          resetForm();
        })
        .catch((err) => {
          console.error('Error creating job:', err.response?.data || err.message);
          alert(err.response?.data?.error || 'Failed to create job. Please try again.');
        });
    }
  };

  const handleDelete = (index) => {
    const jobId = applications[index].id;
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      axios
        .delete(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/${jobId}`)
        .then(() => {
          const updated = [...applications];
          updated.splice(index, 1);
          setApplications(updated);
        })
        .catch((err) => {
          console.error('Error deleting job:', err.response?.data || err.message);
          alert(err.response?.data?.error || 'Failed to delete job. Please try again.');
        });
    }
  };

  const handleEdit = (index) => {
    const job = applications[index];
    setEditIndex(index);
    // Ensure skills is always an array in the form, regardless of what
    // shape it happens to have on the stored record.
    setFormData({
      ...job,
      skills: Array.isArray(job.skills)
        ? job.skills
        : typeof job.skills === 'string' && job.skills.length > 0
        ? job.skills.split(',').map((s) => s.trim())
        : [],
      closingDate: job.closingDate ? job.closingDate.split('T')[0] : '',
      postingDate: job.postingDate
        ? job.postingDate.split('T')[0]
        : new Date().toISOString().split('T')[0],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActiveStatus = (index) => {
    const job = applications[index];
    const updatedJob = {
      ...job,
      isActive: !job.isActive,
      // Keep skills as an array here too, in case the stored record
      // ever has it as a string.
      skills: Array.isArray(job.skills)
        ? job.skills
        : typeof job.skills === 'string' && job.skills.length > 0
        ? job.skills.split(',').map((s) => s.trim())
        : [],
    };

    axios
      .put(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/${job.id}`, updatedJob)
      .then((res) => {
        const updatedApps = [...applications];
        updatedApps[index] = res.data.job;
        setApplications(updatedApps);
      })
      .catch((err) => {
        console.error('Error updating job status:', err.response?.data || err.message);
        alert(err.response?.data?.error || 'Failed to update job status. Please try again.');
      });
  };

  const filteredApplications = applications.filter(
    (app) =>
      app.positionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(app.skills) &&
        app.skills.some((skill) =>
          skill.toLowerCase().includes(searchTerm.toLowerCase())
        ))
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Job Application Management</h1>
        </div>

        {/* Job Form */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              {editIndex !== null ? 'Edit Job Posting' : 'Create New Job Posting'}
            </h2>
          </div>
          <form
            onSubmit={handleApplicationSubmit}
            className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position *</label>
              <input
                type="text"
                name="positionName"
                placeholder="Enter position name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                value={formData.positionName}
                onChange={handleFormChange}
                required
              />
            </div>

            {/* Qualification */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Qualification *</label>
              <input
                type="text"
                name="qualification"
                placeholder="e.g. Bachelor's Degree"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                value={formData.qualification}
                onChange={handleFormChange}
                required
              />
            </div>

            {/* Experience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience *</label>
              <input
                type="text"
                name="experience"
                placeholder="e.g. 3-5 years"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                value={formData.experience}
                onChange={handleFormChange}
                required
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
              <input
                type="text"
                name="location"
                placeholder="e.g. New York, NY"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                value={formData.location}
                onChange={handleFormChange}
                required
              />
            </div>

            {/* Number of Openings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Openings *
              </label>
              <input
                type="number"
                name="numberOfOpenings"
                placeholder="e.g. 2"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                value={formData.numberOfOpenings}
                onChange={handleFormChange}
                required
                min="1"
              />
            </div>

            {/* Posting Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Posting Date *</label>
              <input
                type="date"
                name="postingDate"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                value={formData.postingDate}
                onChange={handleFormChange}
                required
              />
            </div>

            {/* Closing Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Closing Date (optional)
              </label>
              <input
                type="date"
                name="closingDate"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                value={formData.closingDate}
                onChange={handleFormChange}
                min={formData.postingDate}
              />
              <p className="mt-1 text-xs text-gray-500">
                If set, position will automatically deactivate after this date
              </p>
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                disabled={!!formData.closingDate}
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                Active Position
              </label>
            </div>

            {/* Skills */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Skills *
              </label>
              <input
                type="text"
                name="skills"
                placeholder="Enter skills separated by commas (e.g. AutoCAD, Billing, Teamwork)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                value={formData.skills.join(', ')}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    skills: e.target.value.split(',').map((s) => s.trim()),
                  })
                }
              />
              {formData.skills.length > 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  Selected: <span className="font-medium">{formData.skills.join(', ')}</span>
                </p>
              )}
            </div>

            {/* Job Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Description *
              </label>
              <textarea
                name="jobDescription"
                placeholder="Detailed description of the job..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm h-32"
                value={formData.jobDescription}
                onChange={handleFormChange}
                required
              />
            </div>

            {/* Form Buttons */}
            <div className="md:col-span-2 flex justify-end space-x-3">
              {editIndex !== null && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                {editIndex !== null ? 'Update Job Posting' : 'Create Job Posting'}
              </button>
            </div>
          </form>
        </div>

        {/* Job Postings List */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Job Postings</h2>
            <div className="flex space-x-3">
              <input
                type="text"
                placeholder="Search postings..."
                className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                onClick={() => setShowApplications(!showApplications)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
              >
                {showApplications ? 'Hide' : 'Show'} Postings
              </button>
            </div>
          </div>

          {showApplications && (
            <div className="p-6">
              {filteredApplications.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Position
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Posted On
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Experience
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Skills
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase text-center">
                          Openings
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Salary
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredApplications.map((app, index) => {
                        const postingDate = app.postingDate ? new Date(app.postingDate) : null;
                        const closingDate = app.closingDate ? new Date(app.closingDate) : null;
                        const isExpired = closingDate ? new Date() > closingDate : false;
                        const actualStatus = isExpired ? false : app.isActive;

                        return (
                          <tr
                            key={app.id || index}
                            className={`hover:bg-gray-50 ${!actualStatus ? 'bg-gray-100' : ''}`}
                          >
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">
                                {app.positionName || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {app.qualification || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {postingDate ? postingDate.toLocaleDateString() : 'N/A'}
                              {closingDate && (
                                <div className="text-xs text-gray-400">
                                  Closes: {closingDate.toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  actualStatus
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {actualStatus ? 'Active' : 'Closed'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {app.location || 'N/A'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {app.experience || 'N/A'}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-1">
                                {Array.isArray(app.skills) && app.skills.length > 0 ? (
                                  app.skills.map((skill, i) => (
                                    <span
                                      key={i}
                                      className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {skill}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-sm text-gray-500">
                                    No skills specified
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 text-center">
                              {app.numberOfOpenings || '0'}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {app.salary || 'N/A'}
                            </td>
                            <td className="px-6 py-4 text-right text-sm font-medium">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => toggleActiveStatus(index)}
                                  className={`${
                                    actualStatus
                                      ? 'text-yellow-600 hover:text-yellow-800'
                                      : 'text-green-600 hover:text-green-800'
                                  }`}
                                  title={actualStatus ? 'Deactivate' : 'Activate'}
                                >
                                  {actualStatus ? '⏸' : '▶'}
                                </button>
                                <button
                                  onClick={() => handleEdit(index)}
                                  className="text-indigo-600 hover:text-indigo-900"
                                  title="Edit"
                                >
                                  ✏
                                </button>
                                <button
                                  onClick={() => handleDelete(index)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  🗑
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No job postings</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm
                      ? 'No results match your search.'
                      : 'Get started by creating a new job posting.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Apply;